import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup, NgForm, ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestProductEditDialogService } from './request-product-edit-dialog.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { RequestProductDto } from '../../../xatena-api';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-product-edit-dialog',
  templateUrl: './request-product-edit-dialog.component.html',
  styleUrls: ['./request-product-edit-dialog.component.scss'],
  providers: [RequestProductEditDialogService],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    FormlyModule,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    TranslateModule,
  ],
})
export class RequestProductEditDialogComponent implements OnInit {
  subs = new SubSink();

  @ViewChild('frm')
  protected frm?: NgForm;

  public get model$() {
    return this.requestProductEditDialogService.model$;
  }

  public get form(): FormGroup {
    return this.requestProductEditDialogService.form;
  }

  public set form(value: FormGroup) {
    this.requestProductEditDialogService.form = value;
  }

  public get options(): FormlyFormOptions {
    return this.requestProductEditDialogService.options;
  }

  public set options(value: FormlyFormOptions) {
    this.requestProductEditDialogService.options = value;
  }

  public get formFieldAppearance(): string {
    return this.requestProductEditDialogService.formFieldAppearance;
  }

  fields: FormlyFieldConfig[] = [];
  requestTypeId: RequestTypeEnum;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    data: {
      requestId: number;
      requestProductId?: number;
      currency?: string;
      fields?: string[];
      requestTypeId: RequestTypeEnum;
    },
    private dialogRef: MatDialogRef<any>,
    private requestProductEditDialogService: RequestProductEditDialogService,
    private spinnerService: NgxSpinnerService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {
    this.requestProductEditDialogService.key = {
      requestId: data.requestId,
      requestProductId: data.requestProductId,
    };

    this.requestTypeId = data.requestTypeId;
    this.requestProductEditDialogService.currency = data.currency;

    const fields = [
      {
        key: 'title',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _('DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.TITLE.LABEL'),
          translate: true,
        },
      },
      {
        key: 'supplierName',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.SUPPLIER_NAME.LABEL'
          ),
          translate: true,
        },
      },
      {
        key: 'supplierArticleNumber',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.SUPPLIER_ARTICLE_NUMBER.LABEL'
          ),
          translate: true,
        },
      },
      {
        key: 'supplierContactName',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.SUPPLIER_CONTACT_NAME.LABEL'
          ),
          translate: true,
        },
      },
      {
        key: 'supplierContactPhoneNumber',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.SUPPLIER_CONTACT_PHONE_NUMBER.LABEL'
          ),
          translate: true,
        },
      },
      {
        key: 'supplierContactMail',
        type: 'input',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.SUPPLIER_CONTACT_MAIL.LABEL'
          ),
          translate: true,
        },
      },
      {
        className: 'currency-field',
        key: 'priceBaseUnit',
        type: 'currency',
        templateOptions: {
          required: true,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.PRICE_BASE_UNIT.LABEL'
          ),
          translate: true,
          currency: this.requestProductEditDialogService.currency,
        },
      },
      {
        key: 'testAmount',
        type: 'input',
        templateOptions: {
          type: 'number',
          required: false,
          appearance: this.formFieldAppearance,
          label: _(
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.TEST_AMOUNT.LABEL'
          ),
          translate: true,
        },
      },
      {
        key: 'amount',
        type: 'input',
        templateOptions: {
          type: 'number',
          required: true,
          appearance: this.formFieldAppearance,
          label:
            'DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.AMOUNT.LABEL_RT' +
            this.requestTypeId,
          translate: true,
        },
      },
    ];

    // marker for translations
    _('DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.AMOUNT.LABEL_RT6');
    _('DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.AMOUNT.LABEL_RT7');
    _('DEMAND_REQUEST.DETAIL.PRODUCT_DIALOG.FORM.AMOUNT.LABEL_RT11');

    fields.forEach((item, idx) => {
      const key = item.key?.toString() ?? '';
      if (data.fields?.includes(key)) {
        this.fields.push(item);
      }
    });
  }

  ngOnInit(): void {
    this.subs.sink = this.requestProductEditDialogService
      .loadProduct()
      .subscribe();
  }

  private resetFormValidation() {
    this.form.markAsUntouched();
    (this.frm as any).submitted = false;
  }

  private forceFormValidation() {
    this.form.markAsTouched();
    (this.frm as any).submitted = true;
  }

  onSaveClicked() {
    this.forceFormValidation();

    if (this.form.valid) {
      const dto: RequestProductDto = {};
      Object.assign(dto, this.form.value);

      this.spinnerService.show();
      this.subs.sink = this.requestProductEditDialogService
        .saveProduct(dto)
        .subscribe(s => {
          this.resetFormValidation();
          this.spinnerService.hide();
          this.dialogRef.close();
        });
    } else {
      this.snackBar.open(
        this.translateService.instant('SHARED.VALIDATION_ERROR'),
        this.translateService.instant('SHARED.OK')
      );
    }
  }
}
